import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { readNarratives } from '@api/narratives';
import { BannerButton, HelpIcon, IconButtonV2, Layout, Tabs } from '@utilities';

import { HELP_CENTER_BASE_URL } from '../../pages/Narratives/utilities/constants';
import Footer from '../../../../components/Footer';
import CustomTemplates from './components/CustomTemplates';
import InteractiveTile from './components/InteractiveTile';
import ModalBuildNarrative from './components/ModalBuildNarrative';
import Page from '../../layouts/Page';

import styles from './_index.module.scss';

const TABS = [
  { label: 'Numerator', value: 'numerator' },
  { label: 'Custom', value: 'custom' },
];

const Narratives = ({
  hasTemplateAccess = false,
  hideHelpButton = false,
  onBuildYourOwnClick,
  onTemplateSelect,
}) => {
  const [activeTab, setActiveTab] = useState('numerator');
  const [narrativeRoutes, setNarrativeRoutes] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRoute, setSelectedRoute] = useState({});

  useEffect(() => {
    const fetchNarrativeRoutes = async () => {
      try {
        const response = await readNarratives();

        setNarrativeRoutes(response);
      } catch (error) {
        console.error(error);
      }
    };

    fetchNarrativeRoutes();
  }, []);

  const handleTileClick = (route) => {
    if (hasTemplateAccess && typeof onTemplateSelect === 'function') {
      onTemplateSelect({
        activeTab,
        template: route,
      });
      return;
    }

    setSelectedRoute(route);
    setIsModalOpen(true);
  };

  return (
    <Page className={styles['narratives-shell']}>
      <Layout.Column className={styles['narratives']}>
        <div className={styles['narratives-content']}>
          <div className={styles['narrative-header']}>
            <h1>Numerator Narratives</h1>
            <h2>Explore templates to quickly create your data story.</h2>
          </div>
          {hasTemplateAccess && (
            <Tabs
              aria-label="Narrative template source"
              onChange={setActiveTab}
              options={TABS}
              value={activeTab}
              variant="rounded"
            />
          )}
          {hasTemplateAccess && (
            <div
              className={styles['build-your-own-cta']}
              data-testid="narratives-build-your-own-cta"
            >
              <BannerButton
                badgeLabel="New"
                data-testid="narratives-build-your-own-button"
                onClick={onBuildYourOwnClick}
                text="Build your own narrative with topic cards"
              />
            </div>
          )}
          {(!hasTemplateAccess || activeTab === 'numerator') && (
            <>
              <div className={styles['interactive-tiles']}>
                {narrativeRoutes.map((route) => (
                  <InteractiveTile key={route.id} onClick={handleTileClick} route={route} />
                ))}
              </div>
              {isModalOpen && (
                <ModalBuildNarrative route={selectedRoute} setIsModalOpen={setIsModalOpen} />
              )}
            </>
          )}
          {hasTemplateAccess && activeTab === 'custom' && (
            <CustomTemplates
              onSelectTemplate={(template) =>
                onTemplateSelect?.({
                  activeTab,
                  template,
                })
              }
            />
          )}
        </div>
        <Footer />
      </Layout.Column>

      {!hideHelpButton && (
        <IconButtonV2
          aria-label="Help"
          className={styles['help-button']}
          icon={<HelpIcon />}
          onClick={() => window.open(HELP_CENTER_BASE_URL, '_blank', 'noopener,noreferrer')}
          size="small"
          tooltip="Help"
          tooltipProps={{ placement: 'left' }}
          variant="subtle"
        />
      )}
    </Page>
  );
};

Narratives.propTypes = {
  hasTemplateAccess: PropTypes.bool,
  hideHelpButton: PropTypes.bool,
  onBuildYourOwnClick: PropTypes.func,
  onTemplateSelect: PropTypes.func,
};

export default Narratives;
